import { useState, useRef, useEffect } from "react"
import { useTareas } from "../context/TareasContext"
import { PRIORITY_CONFIG } from "../context/TareasContext"
import AssigneeAvatar from "../components/AssigneeAvatar"
import formatWorkerDisplayName from "../utils/formatWorkerDisplayName"
import { FaChevronDown, FaChevronRight, FaPlus, FaClock, FaExpand, FaUserPlus, FaTimes } from "react-icons/fa"
import IconButton from "@mui/material/IconButton"
import DeleteIcon from "@mui/icons-material/Delete"
import Pagination from "@mui/material/Pagination"

const PRIORITIES = ["Baja", "Media", "Alta", "Urgente"]
const PRIORITY_ICONS = { Baja: "↓", Media: "→", Alta: "↑", Urgente: "⚡" }

const formatDate = (date) => {
  if (!date) return null
  const d = new Date(date)
  const months = ["ene","feb","mar","abr","may","jun","jul","ago","sep","oct","nov","dic"]
  return `${String(d.getDate()).padStart(2,"0")} ${months[d.getMonth()]} ${d.getFullYear()}`
}

// --- Título inline ---
const InlineTitle = ({ task, color, openTask }) => {
  const { updateTask } = useTareas()
  const [editing, setEditing] = useState(false)
  const [value, setValue] = useState(task.task)
  const inputRef = useRef(null)

  const commit = async () => {
    setEditing(false)
    const trimmed = value.trim()
    if (trimmed && trimmed !== task.task) await updateTask(task.id, { task: trimmed })
    else setValue(task.task)
  }

  if (editing) return (
    <input
      ref={inputRef}
      value={value}
      onChange={e => setValue(e.target.value)}
      onBlur={commit}
      onKeyDown={e => {
        if (e.key === "Enter") commit()
        if (e.key === "Escape") { setEditing(false); setValue(task.task) }
      }}
      onClick={e => e.stopPropagation()}
      autoFocus
      className="w-full text-xs font-medium text-gray-800 border border-orange-300 rounded-lg px-1.5 py-0.5 focus:outline-none focus:ring-2 focus:ring-orange-300"
    />
  )

  return (
    <div className="flex items-center gap-2 group/title min-w-0">
      <span className="w-1.5 h-1.5 rounded-full flex-shrink-0" style={{ backgroundColor: color }} />
      <span
        className="text-xs font-medium text-gray-800 cursor-text hover:text-orange-600 whitespace-normal break-words flex-1 min-w-0"
        onClick={e => { e.stopPropagation(); setValue(task.task); setEditing(true); setTimeout(() => inputRef.current?.select(), 0) }}
        title="Clic para editar"
      >
        {task.task}
      </span>
      <button
        onClick={e => { e.stopPropagation(); openTask(task) }}
        className="opacity-0 group-hover/title:opacity-100 flex-shrink-0 p-1 text-gray-400 hover:text-orange-500 hover:bg-orange-50 rounded transition-all"
        title="Abrir detalle"
      >
        <FaExpand className="text-[9px]" />
      </button>
    </div>
  )
}

/** Tags de proyecto en la tarea (mismos IDs que `proyectosAdmin.tags`). */
const TaskProjectTagChips = ({ task }) => {
  const { selectedProject } = useTareas()
  const defs = selectedProject?.tags || []
  const ids = (task.projectTags || [])
    .map((x) => (typeof x === "object" && x != null ? x.tagId : x))
    .filter(Boolean)
  if (ids.length === 0) return null
  return (
    <div className="flex flex-wrap gap-0.5 mt-0.5 pl-3.5">
      {ids.map((tid) => {
        const def = defs.find((t) => t.id === tid)
        const label = def?.name || tid.slice(0, 8)
        const col = def?.color || "#9CA3AF"
        return (
          <span
            key={tid}
            className="text-[9px] px-1 py-0 rounded border border-gray-100 max-w-[100px] truncate"
            style={{ color: col, backgroundColor: `${col}18` }}
            title={def?.archived ? `${label} (archivado)` : label}
          >
            {label}
          </span>
        )
      })}
    </div>
  )
}

// --- Prioridad inline ---
const InlinePriority = ({ task }) => {
  const { updateTask } = useTareas()
  const [open, setOpen] = useState(false)
  const ref = useRef(null)

  useEffect(() => {
    const handler = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false) }
    document.addEventListener("mousedown", handler)
    return () => document.removeEventListener("mousedown", handler)
  }, [])

  const config = task.priority ? (PRIORITY_CONFIG[task.priority] ?? { color: "#6B7280", bg: "#F3F4F6" }) : null

  return (
    <div ref={ref} className="relative" onClick={e => e.stopPropagation()}>
      <button
        onClick={() => setOpen(v => !v)}
        className="flex items-center gap-1 rounded-full px-2 py-0.5 text-[10px] font-medium transition-colors hover:opacity-80"
        style={config ? { backgroundColor: config.bg, color: config.color } : { backgroundColor: "#F3F4F6", color: "#9CA3AF" }}
      >
        {task.priority ? <><span>{PRIORITY_ICONS[task.priority]}</span>{task.priority}</> : <span>—</span>}
      </button>
      {open && (
        <div className="absolute left-0 top-full mt-1 bg-white border border-gray-200 rounded-xl shadow-lg z-50 p-1.5 flex flex-col gap-0.5 min-w-[110px]">
          <button onClick={() => { updateTask(task.id, { priority: "" }); setOpen(false) }} className="text-left text-[10px] px-2 py-1 rounded-lg hover:bg-gray-50 text-gray-400">Sin prioridad</button>
          {PRIORITIES.map(p => {
            const cfg = PRIORITY_CONFIG[p]
            return (
              <button
                key={p}
                onClick={() => { updateTask(task.id, { priority: p }); setOpen(false) }}
                className="text-left text-[10px] px-2 py-1 rounded-lg flex items-center gap-1.5 font-medium"
                style={{ color: cfg.color, backgroundColor: task.priority === p ? cfg.bg : "transparent" }}
              >
                <span>{PRIORITY_ICONS[p]}</span>{p}
              </button>
            )
          })}
        </div>
      )}
    </div>
  )
}

// --- Estado inline ---
const InlineStatus = ({ task }) => {
  const { updateTask, getStatusId, getTaskStatusValue, estadosTareas } = useTareas()
  const [open, setOpen] = useState(false)
  const ref = useRef(null)

  useEffect(() => {
    const handler = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false) }
    document.addEventListener("mousedown", handler)
    return () => document.removeEventListener("mousedown", handler)
  }, [])

  const currentName = getTaskStatusValue(task)
  const current = estadosTareas.find(e => e.name === currentName)

  return (
    <div ref={ref} className="relative" onClick={e => e.stopPropagation()}>
      <button
        onClick={() => setOpen(v => !v)}
        className="flex items-center gap-1.5 rounded-full px-2.5 py-1 text-[10px] font-medium transition-all hover:opacity-80 border"
        style={current
          ? { backgroundColor: `${current.color}18`, color: current.color, borderColor: `${current.color}40` }
          : { backgroundColor: "#F3F4F6", color: "#9CA3AF", borderColor: "#E5E7EB" }
        }
      >
        {current && <span className="w-1.5 h-1.5 rounded-full flex-shrink-0" style={{ backgroundColor: current.color }} />}
        {currentName || "Sin estado"}
      </button>
      {open && (
        <div className="absolute left-0 top-full mt-1 bg-white border border-gray-200 rounded-xl shadow-lg z-50 p-1.5 flex flex-col gap-0.5 min-w-[160px]">
          {estadosTareas.map(e => (
            <button
              key={e.id}
              onClick={() => {
                const id = getStatusId(e.name)
                if (id) updateTask(task.id, { status: id })
                setOpen(false)
              }}
              className="flex items-center gap-2 text-[10px] px-2.5 py-1.5 rounded-lg transition-colors text-left font-medium"
              style={{
                color: e.color || "#374151",
                backgroundColor: currentName === e.name ? `${e.color}15` : "transparent",
              }}
              onMouseEnter={ev => ev.currentTarget.style.backgroundColor = `${e.color}18`}
              onMouseLeave={ev => ev.currentTarget.style.backgroundColor = currentName === e.name ? `${e.color}15` : "transparent"}
            >
              <span className="w-2 h-2 rounded-full flex-shrink-0" style={{ backgroundColor: e.color || "#6B7280" }} />
              {e.name}
              {currentName === e.name && <span className="ml-auto text-[9px] opacity-60">✓</span>}
            </button>
          ))}
        </div>
      )}
    </div>
  )
}

// --- Asignados inline ---
const InlineAssignees = ({ task }) => {
  const { updateTask, workers } = useTareas()
  const [open, setOpen] = useState(false)
  const ref = useRef(null)

  useEffect(() => {
    const handler = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false) }
    document.addEventListener("mousedown", handler)
    return () => document.removeEventListener("mousedown", handler)
  }, [])

  const assignees = task.assignees || []
  const assigneeIds = assignees.map(a => (typeof a === "object" ? a.id : a)).filter(Boolean)
  const available = workers.filter(w => !assigneeIds.includes(w.id))

  const add = async (workerId) => {
    if (!workerId) return
    await updateTask(task.id, { assignees: [...assigneeIds, workerId] })
    setOpen(false)
  }
  const remove = async (id) => {
    await updateTask(task.id, { assignees: assigneeIds.filter(x => x !== id) })
  }

  return (
    <div ref={ref} className="relative flex items-center gap-1 flex-wrap" onClick={e => e.stopPropagation()}>
      {assignees.map(a => {
        const id = typeof a === "object" ? a.id : a
        const name = typeof a === "object" ? a.name : workers.find(w => w.id === id)?.name || id
        return (
          <div key={id} className="group/av relative">
            <AssigneeAvatar name={name} size={20} user={typeof a === "object" ? a : workers.find(w => w.id === id)} />
            <button
              onClick={e => { e.stopPropagation(); remove(id) }}
              className="absolute -top-1 -right-1 w-3 h-3 bg-red-500 text-white rounded-full items-center justify-center text-[7px] hidden group-hover/av:flex"
            >
              <FaTimes />
            </button>
          </div>
        )
      })}
      {available.length > 0 && (
        <>
          <button
            onClick={() => setOpen(v => !v)}
            className="w-5 h-5 rounded-full border border-dashed border-gray-300 flex items-center justify-center text-gray-400 hover:border-orange-400 hover:text-orange-400 transition-colors"
          >
            <FaUserPlus className="text-[8px]" />
          </button>
          {open && (
            <div className="absolute left-0 top-full mt-1 bg-white border border-gray-200 rounded-xl shadow-lg z-50 p-1.5 flex flex-col gap-0.5 min-w-[150px]">
              {available.map(w => (
                <button
                  key={w.id}
                  onClick={() => add(w.id)}
                  className="flex items-center gap-2 text-[10px] px-2 py-1.5 rounded-lg hover:bg-orange-50 text-gray-700"
                >
                  <AssigneeAvatar name={w.name} size={16} user={w} />
                  {formatWorkerDisplayName(w.name || w.email)}
                </button>
              ))}
            </div>
          )}
        </>
      )}
    </div>
  )
}

// --- Fila de tarea ---
const TaskRow = ({ task, color, openTask, deleteTask }) => {
  const isPast = task.limitDate && new Date(task.limitDate) < new Date()
  return (
    <div className="grid grid-cols-[1fr_140px_110px_120px_160px_40px] gap-2 px-3 py-2 items-start border-b border-gray-50 hover:bg-orange-50/20 transition-colors group/row">
      <div className="min-w-0 flex flex-col">
        <InlineTitle task={task} color={color} openTask={openTask} />
        <TaskProjectTagChips task={task} />
      </div>
      <InlineAssignees task={task} />
      <InlinePriority task={task} />
      <span className={`text-[10px] flex items-center gap-1 flex-shrink-0 ${isPast ? "text-red-500 font-medium" : "text-gray-400"}`}>
        {task.limitDate ? <><FaClock className="text-[8px]" />{formatDate(task.limitDate)}</> : "—"}
      </span>
      <InlineStatus task={task} />
      <div className="flex justify-center opacity-0 group-hover/row:opacity-100 transition-opacity">
        <IconButton size="small" onClick={e => { e.stopPropagation(); deleteTask(task.id) }}>
          <DeleteIcon fontSize="inherit" sx={{ color: "#EF4444" }} />
        </IconButton>
      </div>
    </div>
  )
}

// --- Grupo ---
const GroupSection = ({ group, openTask, deleteTask, onCreateTask }) => {
  const [collapsed, setCollapsed] = useState(false)

  return (
    <div className="mb-3">
      <button
        onClick={() => setCollapsed(v => !v)}
        className="flex items-center gap-2 w-full px-3 py-2 hover:bg-gray-50 rounded-lg transition-colors"
      >
        <span className="text-gray-400 text-xs">
          {collapsed ? <FaChevronRight /> : <FaChevronDown />}
        </span>
        <span className="w-2.5 h-2.5 rounded-full flex-shrink-0" style={{ backgroundColor: group.color }} />
        <span className="text-sm font-semibold text-gray-700">{group.label}</span>
        <span className="text-xs text-gray-400 font-normal ml-1">({group.tasks.length})</span>
      </button>

      {!collapsed && (
        <div className="ml-4 mt-0.5">
          <div className="grid grid-cols-[1fr_140px_110px_120px_160px_40px] gap-2 px-3 py-1.5 text-[10px] font-semibold text-gray-400 uppercase tracking-wider border-b border-gray-100">
            <span>Tarea</span>
            <span>Asignados</span>
            <span>Prioridad</span>
            <span>Fecha límite</span>
            <span>Estado</span>
            <span />
          </div>

          {group.tasks.map(task => (
            <TaskRow
              key={task.id}
              task={task}
              color={group.color}
              openTask={openTask}
              deleteTask={deleteTask}
            />
          ))}

          <button
            onClick={() => onCreateTask?.()}
            className="flex items-center gap-2 px-3 py-2 text-xs text-gray-400 hover:text-orange-500 transition-colors w-full"
          >
            <FaPlus className="text-[9px]" /> Nueva tarea en {group.label}
          </button>
        </div>
      )}
    </div>
  )
}

const ListView = () => {
  const {
    tasks, openTask, deleteTask,
    totalPages, totalDocs, page, limit,
    handlePageChange, handleLimitChange,
    openCreateModal,
    getTaskStatusId,
    estadosTareas,
    workers,
  } = useTareas()

  const [groupBy, setGroupBy] = useState("status")

  const groups = (() => {
    if (groupBy === "status") {
      const byEstado = estadosTareas.map(e => ({
        id: e.id,
        label: e.name,
        color: e.color || "#6B7280",
        tasks: tasks.filter(t => String(getTaskStatusId(t)) === String(e.id)),
      })).filter(g => g.tasks.length > 0)
      const assignedIds = new Set(byEstado.flatMap(g => g.tasks.map(t => t.id)))
      const sinEstado = tasks.filter(t => !assignedIds.has(t.id))
      if (sinEstado.length > 0) {
        byEstado.push({ id: "__sin_estado__", label: "Sin estado", color: "#9CA3AF", tasks: sinEstado })
      }
      return byEstado
    }
    if (groupBy === "priority") {
      const priorities = ["Urgente", "Alta", "Media", "Baja", "Sin prioridad"]
      const colors = { Urgente: "#EF4444", Alta: "#F59E0B", Media: "#3B82F6", Baja: "#6B7280", "Sin prioridad": "#9CA3AF" }
      return priorities.map(p => ({
        id: p, label: p, color: colors[p],
        tasks: tasks.filter(t => (t.priority || "Sin prioridad") === p),
      })).filter(g => g.tasks.length > 0)
    }
    if (groupBy === "worker") {
      const byWorker = {}
      tasks.forEach((t) => {
        const a0 = t.assignees?.[0]
        const key =
          a0 == null ? "__sin_asignar__" : typeof a0 === "object" ? String(a0.id) : String(a0)
        if (!byWorker[key]) byWorker[key] = []
        byWorker[key].push(t)
      })
      return Object.entries(byWorker).map(([idKey, ts]) => {
        if (idKey === "__sin_asignar__") {
          return { id: idKey, label: "Sin asignar", color: "#6B7280", tasks: ts }
        }
        const w = workers.find((x) => String(x.id) === idKey)
        const embedded = ts[0]?.assignees?.[0]
        const rawName =
          typeof embedded === "object" && embedded?.name
            ? embedded.name
            : w?.name || w?.email || idKey
        return {
          id: idKey,
          label: formatWorkerDisplayName(rawName) || idKey,
          color: "#6B7280",
          tasks: ts,
        }
      })
    }
    return [{ id: "all", label: "Todas", color: "#FE6400", tasks }]
  })()

  return (
    <div className="flex flex-col h-full">
      {/* Toolbar */}
      <div className="flex items-center gap-3 px-4 py-2 bg-gray-50 border-b border-gray-100">
        <span className="text-xs text-gray-500 font-medium">Agrupar por</span>
        <div className="flex gap-1">
          {[
            { id: "status",   label: "Estado" },
            { id: "priority", label: "Prioridad" },
            { id: "worker",   label: "Responsable" },
            { id: "none",     label: "Sin grupo" },
          ].map(opt => (
            <button
              key={opt.id}
              onClick={() => setGroupBy(opt.id)}
              className={`text-xs px-2.5 py-1 rounded-lg transition-colors ${
                groupBy === opt.id
                  ? "bg-orange-500 text-white"
                  : "bg-white border border-gray-200 text-gray-500 hover:border-gray-300"
              }`}
            >
              {opt.label}
            </button>
          ))}
        </div>
      </div>

      {/* Lista */}
      <div className="flex-1 overflow-y-auto px-4 py-3">
        {groups.map(group => (
          <GroupSection
            key={group.id}
            group={group}
            openTask={openTask}
            deleteTask={deleteTask}
            onCreateTask={() =>
              openCreateModal(groupBy === "status" && group.id !== "__sin_estado__" ? group.label : null)
            }
          />
        ))}

        {tasks.length === 0 && (
          <div className="flex items-center justify-center h-40 text-gray-400 text-sm">
            No hay tareas que mostrar
          </div>
        )}

        <button
          onClick={() => openCreateModal()}
          className="flex items-center gap-2 text-xs text-gray-400 hover:text-orange-500 transition-colors mt-2 px-3"
        >
          <FaPlus className="text-[10px]" /> Nueva tarea
        </button>
      </div>

      {/* Paginación */}
      <div className="flex items-center justify-between px-5 py-3 border-t border-gray-100 bg-white flex-shrink-0">
        <div className="flex items-center gap-3">
          <span className="text-xs text-gray-500">
            Mostrando <strong>{tasks.length}</strong> de <strong>{totalDocs}</strong> tareas
          </span>
          <select
            value={limit}
            onChange={e => handleLimitChange(Number(e.target.value))}
            className="text-xs border border-gray-200 rounded-lg px-2 py-1 text-gray-600 focus:outline-none"
          >
            {[15, 25, 35, 50].map(n => <option key={n} value={n}>{n} por página</option>)}
          </select>
        </div>
        <Pagination
          count={totalPages}
          page={page}
          onChange={handlePageChange}
          variant="outlined"
          shape="rounded"
          size="small"
        />
      </div>
    </div>
  )
}

export default ListView
